<i18n src="./resources/locales.json"></i18n>

<template>
  <section class="main-page">
    <EcsIdleTimer v-if="isIdleTimeRedirectEnabled" v-slot="{}" :duration="idleTimeToRedirect" @idle="handleIdle" />
    <EcsDesktopNotification v-if="isDesktopNotificationEnabled" v-slot="{}" />

    <EcsSplashScreen v-if="showSplashScreen" is-loading />
    <div v-else id="inspire">
      <!-- Login Page -->
      <EcsLogin v-if="!loggedInAgent" ref="login" :title="logoutTitle" :message="logoutMessage" />
      <!-- Application for an Authorized User -->
      <div v-else-if="isRegistered">
        <div v-if="!isWallboard">
          <!-- Application Header -->
          <EcsAppHeader @user-logout-request="manualLogout" />

          <!-- Application Menu & Sidebar -->
          <EcsAppMenu @mini-mode-disabled="handleMiniMode" />

          <!-- Application Content -->
          <v-main
            ref="ecsInteractionContainer"
            class="ecs-interaction-container"
            :dark="$vuetify.theme.dark"
            :offline="isOfflineModeEnabled"
          >
            <!-- Incoming Interaction Manager-->
            <EcsIncomingInteractionManager v-if="displayPendingInteractionBanner" />

            <v-container id="ecs-page-container" class="custom-scrollbar" fluid :style="computedHeight">
              <router-view />
              <v-overlay :value="!$socket.connected" absolute z-index="109">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
              </v-overlay>
            </v-container>

            <EcsAgentStats />
          </v-main>
        </div>
        <div v-else-if="isWallboard">
          <EcsWallboardHeader @user-logout-request="manualLogout" />
          <EcsWallboardPage />
        </div>
      </div>

      <EcsCrmCallbackSection />
      <EcsUserIntroduction />
      <EcsSnackbar />
      <EcsContextMenu />
      <EcsCrmContactCardSingleton />
      <EcsContactCardSingleton />
      <EcsContactCard />
      <EcsAgentStatsSummaryCardSingleton />
      <EcsInteractionContextMenu />
      <EcsBusinessProcessSelectionDialog />
      <EcsContactPhoneSelectorPopUp />
      <EcsSoftPhone v-if="isOnline" />
    </div>

    <EcsTheme />
    <EcsConfirmationDialog
      ref="overrideSessionConfirmationDialog"
      :title="$t('loginRemovingRemoteSessionTitle')"
      :message="$t('loginRemovingRemoteSession')"
      :confirm-button-text="$t('actions.continue')"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import jwtDecode from 'jwt-decode';
import { v4 as uuid } from 'uuid';
import { debounce } from 'lodash';
import { CONTACT_TYPE } from '@/constants/contactConstants';
import { MEDIA_TYPE_TELEPHONY, MEDIA_TYPE_CHAT, MEDIA_TYPE_EMAIL, MEDIA_TYPE_SMS } from '@/constants/socket-events';
import EcsInteraction from '@/components/ecs-interaction/EcsInteraction';
import EcsAgentStats from '@/components/ecs-agent-stats/EcsAgentStats';
import EcsAppHeader from '@/components/ecs-app-header/EcsAppHeader';
import EcsAppMenu from '@/components/ecs-app-menu/EcsAppMenu';
import EcsIncomingInteractionManager from '@/components/ecs-incoming-interaction-manager/EcsIncomingInteractionManager';
import EcsLogin from '@/components/ecs-login/EcsLogin';
import EcsSplashScreen from '@/components/ecs-splash-screen/EcsSplashScreen';
import EcsSnackbar from '@/components/ecs-snackbar/EcsSnackbar';
import EcsTheme from '@/components/ecs-theme/EcsTheme';
import ThemeUtility from '@/components/ecs-theme/ecs-theme-utility';
import EcsCrmCallbackSection from '@/components/ecs-base-interaction-components/ecs-interaction-crm-panel/ecs-crm-callback-section/EcsCrmCallbackSection';
import EcsIdleTimer from '@/components/ecs-idle-timer/EcsIdleTimer';
import { ELEMENT_STATE } from '@/constants/element/constants';
import { INTERACTION_STATE } from '@/constants/interaction/constants';
import EcsUserIntroduction from '@/components/ecs-user-introduction/EcsUserIntroduction';
import EcsDesktopNotification from '@/components/ecs-desktop-notification/EcsDesktopNotification';
import EcsContextMenu from '@/components/ecs-context-menu/EcsContextMenu';
import EcsCrmContactCardSingleton from '@/components/ecs-interaction/EcsCrmContactCardSingleton';
import EcsContactCardSingleton from '@/components/ecs-contacts-directory/EcsContactCardSingleton';
import EcsInteractionContextMenu from '@/components/ecs-interaction/EcsInteractionContextMenu';
import EcsAgentStatsSummaryCardSingleton from '@/components/ecs-agent-info/agent-stats-summary-card-singleton/EcsAgentStatsSummaryCardSingleton';
import AgentService from '@/services/agentService';
import SupervisorService from '@/services/supervisorService';
import HttpRequestHandler from '@/plugins/http';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';
import EcsBusinessProcessSelectionDialog from '@/components/ecs-business-process-selection-dialog/EcsBusinessProcessSelectionDialog';
import EcsSoftPhone from '@/components/ecs-soft-phone/EcsSoftPhone';
import LogService from '@/services/logService';
import EcsWallboardPage from '@/pages/ecs-wallboard/EcsWallboardPage';
import EcsWallboardHeader from '@/components/ecs-wallboard-header/EcsWallboardHeader';
import EcsSoftPhoneMixin from '@/components/ecs-soft-phone/EcsSoftPhoneMixin';
import EcsContactCard from '@/components/ecs-contact-card/EcsContactCard';
import EcsContactPhoneSelectorPopUp from '@/components/ecs-contacts-directory/EcsContactPhoneSelectorPopUp';
import MSTeamsMixing from '@/mixins/ms-teams/MSTeamsMixing';
import { ACTIONS } from '@/constants/softPhone/constants';
import { TERMINAL_TYPES } from '@/constants/terminalTypes';

export default {
  name: 'EcsMainApp',

  components: {
    EcsContactPhoneSelectorPopUp,
    EcsContactCard,
    EcsWallboardPage,
    EcsWallboardHeader,
    EcsConfirmationDialog,
    EcsBusinessProcessSelectionDialog,
    EcsAgentStatsSummaryCardSingleton,
    EcsContextMenu,
    EcsCrmCallbackSection,
    EcsAgentStats,
    EcsAppHeader,
    EcsAppMenu,
    EcsIncomingInteractionManager,
    EcsLogin,
    EcsSnackbar,
    EcsSplashScreen,
    EcsTheme,
    EcsIdleTimer,
    EcsUserIntroduction,
    EcsDesktopNotification,
    EcsCrmContactCardSingleton,
    EcsContactCardSingleton,
    EcsInteractionContextMenu,
    EcsSoftPhone
  },

  mixins: [EcsSoftPhoneMixin, MSTeamsMixing],

  props: {
    source: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      $socket: null,
      isWallboard: false,
      isRestoringInteractions: false,
      isAuthenticatingToken: true,
      isAuthenticatingEvents: false,
      isRegistering: false,
      isRegistered: false,
      isLoadingLoggedInAgentData: false,
      isLoadingAppSettings: false,
      logoutTitle: '',
      logoutMessage: '',
      registerRequestRef: null,
      registerRetryRef: null,
      registerRetryCounter: 0,
      initializeUserRetryCounter: 0,
      socketReconnectionAttemptCounter: 0,
      isDesktopNotificationEnabled: false,
      listenerFn: e => e.preventDefault(),
      span: null,
      isOnline: true
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent', 'isSupervisor', 'registrationId', 'telephoneTerminals']),

    ...mapGetters('CONTACTS', ['getInternalBusinessContact']),

    ...mapGetters('CONVERSATIONS', ['getConversationsForInteraction']),

    ...mapGetters('ELEMENTS', [
      'getRawElementsForInteraction',
      'getElementIdsByInteractionId',
      'getElementsForInteraction',
      'getElementAnsweredAt'
    ]),

    ...mapGetters('INTERACTIONS', ['getActiveInteraction', 'getActiveTelephonyInteractions', 'getPendingInteractions']),

    ...mapGetters('SETTINGS', [
      'isOfflineModeEnabled',
      'preferencesSettings',
      'applicationSettings',
      'agentStateSettings',
      'lastUserSettingsUpdate'
    ]),

    ...mapGetters('ORGANIZATION', ['organizationData', 'orgId', 'orgName', 'orgShortName', 'orgKafkaTopicName']),

    showSplashScreen() {
      return this.isAuthenticatingToken;
    },

    computedHeight() {
      const offset = this.isOfflineModeEnabled ? 142 : 112;
      return {
        height: `calc(100vh - ${offset}px)`
      };
    },

    displayPendingInteractionBanner() {
      return this.preferencesSettings.incomingInteractionDisplay !== 'panel';
    },

    isIdleTimeRedirectEnabled() {
      return (
        !/dashboards/i.test(this.$route.name) &&
        this.preferencesSettings.idleTimeToAgentIntelligenceCenter &&
        this.preferencesSettings.idleTimeToAgentIntelligenceCenter !== -1 &&
        this.$router.currentRoute.name !== 'Supervisor'
      );
    },

    idleTimeToRedirect() {
      return this.isIdleTimeRedirectEnabled
        ? this.preferencesSettings.idleTimeToAgentIntelligenceCenter
        : Number.MAX_VALUE;
    }
  },

  watch: {
    'preferencesSettings.pauseKPIEvents': {
      immediate: true,
      handler() {
        this.setPauseKPIEvents();
      }
    },

    isSupervisor() {
      this.handleSupervisorTitle();
    }
  },

  created() {
    this.$root.$on('restore-active-interactions', this.restoreActiveInteractions);
    this.$root.$on('re-register', this.initialize);
    this.$root.$on('register', this.registerUser);
    this.$root.$on('ecs-error', this.handleServerSideError);
    this.$root.$on('log-out', this.fullLogOut);
    this.$root.$on('soft-phone-component-created', this.initializeSoftPhone);

    window.addEventListener('online', this.updateOnlineStatus);
    window.addEventListener('offline', this.updateOnlineStatus);
    window.addEventListener('beforeunload', this.beforeunload);
  },

  mounted() {
    this.initializeUser();
  },

  beforeDestroy() {
    this.$root.$off('restore-active-interactions', this.restoreActiveInteractions);
    this.$root.$off('re-register', this.initialize);
    this.$root.$off('register', this.registerUser);
    this.$root.$off('ecs-error', this.handleServerSideError);
    this.$root.$off('log-out', this.fullLogOut);
    this.$root.$off('soft-phone-component-created', this.initializeSoftPhone);

    window.removeEventListener('online', this.updateOnlineStatus);
    window.removeEventListener('offline', this.updateOnlineStatus);
  },

  sockets: {
    authenticated() {
      this.isAuthenticatingEvents = false;
      this.initialize();
    },

    reconnect() {
      this.disableOfflineMode();
      this.clearAllEventTimingConstraints();
      this.initializeSocket();
    },

    reconnecting() {
      if (this.socketReconnectionAttemptCounter > 1 && !this.isOfflineModeEnabled) {
        this.enableOfflineMode();
      }
    },

    disconnect() {
      this.isAuthenticatingEvents = false;
      this.isAuthenticatingToken = false;
    },

    connect() {
      this.initializeSocket();
    },

    error() {
      this.handleLogout(this.$t('logoutDueToServerIssues'));
    },

    reconnect_attempt(count) {
      this.socketReconnectionAttemptCounter = count;
    },

    authenticatedInAnotherLocation() {
      this.handleLogout(this.$t('logoutFromRemoteLocation'));
    },

    /**
     * Heartbeat event to keep the session alive.
     **/
    postKeepAlive() {
      if (this.isWallboard) {
        return;
      }

      const browserId = sessionStorage.getItem('AWC_BROWSER_ID');
      const preventServerSync = Math.floor(Math.random() * 5000);

      setTimeout(() => {
        AgentService.postKeepAlive({ instanceId: browserId });
      }, preventServerSync);
    },

    CONFIGURATION_CHANGED_EVENT(event) {
      this.updateOrgConfiguration({
        versionId: event.versionId,
        callback: () => {
          this.retrieveLoggedInAgentData().then(() => {
            this.initialize({ isConfigurationUpdate: true });
          });
        }
      });
    },

    async AGENT_REGISTER_OTHER_USER_EVENT(event) {
      await this.registerRequestRef;
      if (this.registrationId !== event.registrationId) {
        this.handleLogout(this.$t('logoutFromRemoteLocation'));
      } else {
        this.checkRegisterState().then(this.setPauseKPIEvents);
      }
    },

    INTERACTION_CREATED_OUTGOING_EVENT(payload) {
      this.routeUserToInteractionTab(payload);
    },

    INTERACTION_ANSWERED_EVENT(payload) {
      this.routeUserToInteractionTab(payload);
    },

    INTERACTION_ACCEPTED_EVENT(payload) {
      this.routeUserToInteractionTab(payload);
    },

    INTERACTION_REMOVED_FROM_AGENT_EVENT(payload) {
      if (payload.interactionStateId === EcsInteraction.interactionState.missed) {
        this.$root.$emit('snackbar-message', {
          message: 'System: The interaction was either abandoned or removed from your agent.',
          type: 'info',
          timeout: 5000
        });
      }
    },

    INTERACTION_ENDED_EVENT(payload) {
      this.moveActiveInteractionToHistory(payload);

      // API takes some seconds to return the latest interactions.
      setTimeout(() => {
        this.restoreActiveInteractions();
        this.requestAgentInteractionHistory({ latestInteractions: true }).catch(console.log);
      }, 10000);
    },

    AGENT_BP_ASSIGNMENT_CHANGED_EVENT(data) {
      if (data.agentId === this.loggedInAgent.id) {
        this.updateAgentAssignedBusinessProcessIds(data);
      }
    }
  },

  methods: {
    ...mapActions('AGENT', [
      'loginAgent',
      'retrieveWallboardUser',
      'retrieveLoggedInAgentData',
      'setLoggedInAgent',
      'setRegistrationId',
      'removeLoggedInAgent',
      'updateLoggedInAgentStateIfNeeded',
      'updateSoftPhoneRegisteredState',
      'updateAgentAssignedBusinessProcessIds'
    ]),

    ...mapActions('BUSINESS_PROCESSES', ['requestAvailableBusinessProcesses', 'requestSupervisedBusinessProcesses']),

    ...mapActions('CAMPAIGNS', ['requestCampaigns']),

    ...mapActions('CONVERSATIONS', ['addSystemMessage', 'restoreConversation']),

    ...mapActions('ELEMENTS', ['removeElement', 'addElement', 'clearInactiveElementsAnsweredAt']),

    ...mapActions('EVENT_TIMING', [
      'clearAllEventTimingConstraints',
      'removeEventTimingConstraintForAgentState',
      'removeEventTimingConstraintForAgentTerminal'
    ]),

    ...mapActions('INTERACTIONS', [
      'removeActiveInteraction',
      'editActiveInteraction',
      'addActiveInteraction',
      'removePendingInteraction',
      'requestAgentInteractionHistory',
      'moveActiveInteractionToHistory',
      'upsertPendingInteraction'
    ]),

    ...mapActions('INTERACTIONS/emails', ['restoreEmail']),

    ...mapActions('NOTIFICATIONS', ['dismissAllSystemNotifications']),

    ...mapActions('REFERENCES', ['requestIncomingInteractionRingtones']),

    ...mapActions('CONTACTS', ['loadAgentStateBatch']),

    ...mapActions('STATS', ['clearStats']),

    ...mapActions('SETTINGS', [
      'disableOfflineMode',
      'enableOfflineMode',
      'requestCurrentUserSettings',
      'removeUserSettings'
    ]),

    ...mapActions('ORGANIZATION', ['restoreOrganizationData', 'updateOrgConfiguration', 'clearOrganizationData']),

    ...mapActions('SYSTEM', ['restoreSystemData']),
    /**
     * Initializes User Session.
     *
     * 3 Possible scenarios.
     *    a. Login with JWT. methods/handleLoginUsingJwt
     *    b. Restore session from storage. methods/handleSessionRestore
     *    c. Session timeout. methods/handleLogout
     **/
    initializeUser() {
      const urlParams = new URLSearchParams(window.location.search);
      const jwt = urlParams.get('jwt');
      const sessionId = sessionStorage.getItem('sessionId');

      let browserId = sessionStorage.getItem('AWC_BROWSER_ID');
      if (!browserId) {
        browserId = uuid();
        sessionStorage.setItem('AWC_BROWSER_ID', browserId);
      }

      if (jwt) {
        this.handleLoginUsingJwt(jwt);
      } else if (sessionId) {
        this.handleSessionRestore();
      } else {
        this.isAuthenticatingToken = false;
        this.handleLogout();
      }
    },

    /**
     * a. Login with JWT
     **/
    handleLoginUsingJwt(jwt) {
      let token = null;

      try {
        token = jwtDecode(jwt);
      } catch (error) {
        this.logoutReason({ reason: 'Invalid JWT token. eAgent automated log out.' });
        this.isAuthenticatingToken = false;
        return this.handleLogout(
          this.$t('logout.errors.invalidToken.message'),
          this.$t('logout.errors.invalidToken.title')
        );
      }

      const browserId = sessionStorage.getItem('AWC_BROWSER_ID');

      const connectionHandler = async ({ activeSession, sameBrowserId }) => {
        if (activeSession) {
          if (sameBrowserId) {
            await this.login(jwt);
          } else {
            const confirmed = await this.$refs.overrideSessionConfirmationDialog.open();

            if (confirmed) {
              await this.login(jwt);
            } else {
              this.logoutReason({ reason: 'Avoid stealing session. eAgent automated log out.' });
              if (this.isWallboard) {
                this.handleLogout('', this.$t('wallboardLogoutTitle'));
              } else {
                this.handleLogout('', this.$t('clientLogoutTitle'));
              }
            }
          }
        } else {
          await this.login(jwt);
        }
      };
      this.$socket.open();
      this.$socket.emit('checkAgentConnection', { account: token.username, browserId }, connectionHandler);
    },

    /**
     * b. Restore session from storage
     **/
    handleSessionRestore() {
      this.isWallboard = false;
      this.isAuthenticatingToken = true;
      const agentSessionData = JSON.parse(sessionStorage.getItem('agent'));

      if (agentSessionData && agentSessionData.isWallboard) {
        return this.retrieveWallboardUser()
          .then(() => {
            this.restoreOrganizationData();
          })
          .finally(() => {
            this.isWallboard = true;
            this.isRegistered = true;
            this.isAuthenticatingToken = false;
          });
      }

      return this.retrieveLoggedInAgentData()
        .then(user => {
          this.restoreOrganizationData();
          this.restoreSystemData();
          this.initializeSocket();
          this.initializeSettings();
          if (process.env.VUE_APP_ELASTIC_APM_ENABLED === 'true') {
            this.$apm.setUserContext({ username: user.account });
          }
        })
        .catch(() => {
          this.logoutReason({ reason: 'Could not retrieve current agent data. eAgent automated log out.' });
          this.handleLogout(this.$t('logout.errors.noCurrentUser'));
        })
        .finally(() => {
          this.isAuthenticatingToken = false;
        });
    },

    async manualLogout() {
      await this.logoutReason({ reason: 'Manual log out.' });

      if (this.isWallboard) {
        this.handleLogout(this.$t('clientLogoutMessage'), this.$t('wallboardLogoutTitle'));
      } else {
        const browserId = sessionStorage.getItem('AWC_BROWSER_ID');
        AgentService.unRegister({ instanceId: browserId })
          .then(() => {
            this.handleLogout(this.$t('clientLogoutMessage'), this.$t('clientLogoutTitle'));
          })
          .catch(() => {});
      }
    },

    /**
     * c. Handle Logout.
     **/
    handleLogout(message = '', title = '') {
      this.dismissAllSystemNotifications();
      this.logoutMessage = message;
      this.logoutTitle = title;
      this.logout();
    },

    logoutReason({ reason }) {
      const payload = { reason };
      if (this.loggedInAgent) {
        payload.enterpriseId = this.loggedInAgent && this.loggedInAgent.pbxEnterpriseID;
        payload.agentId = this.loggedInAgent && this.loggedInAgent.id;
        payload.account = this.loggedInAgent && (this.loggedInAgent.account || this.loggedInAgent.username);
        payload.orgKafkaTopicName = this.orgKafkaTopicName;
        payload.orgName = this.orgShortName;
        payload.orgId = this.orgId;
      }

      return LogService.logoutReason(payload);
    },

    updateOnlineStatus() {
      this.isOnline = navigator.onLine;

      if (navigator.onLine) {
        this.initialize();
        this.disableOfflineMode();
      } else {
        this.enableOfflineMode();
        this.updateSoftPhoneRegisteredState(false);
      }
    },

    checkRegisterState() {
      if (this.isSupervisor && this.$featuresManager.supervisorFeatures.enable) {
        return SupervisorService.isSupervisorRegistered().catch(() => {
          this.handleLogout(this.$t('logoutFromRemoteLocation'));
        });
      } else {
        return AgentService.isAgentRegistered().catch(() => {
          this.handleLogout(this.$t('logoutFromRemoteLocation'));
        });
      }
    },

    initialize(props) {
      if (this.initializeUserRetryCounter > 3) {
        return this.handleLogout();
      }

      if (!this.loggedInAgent) {
        return;
      }

      this.clearAllEventTimingConstraints();
      this.removeEventTimingConstraintForAgentState({
        agentId: this.loggedInAgent.id
      });

      if (props && props.showConnectionRestoredMessage) {
        setTimeout(() => {
          this.$root.$emit('snackbar-message', {
            message: 'Your connection was restored',
            type: 'info',
            timeout: 5000
          });
        }, 2000);
      }

      this.isLoadingLoggedInAgentData = true;

      this.retrieveLoggedInAgentData()
        .then(() => {
          this.initializeUserRetryCounter = 0;
          this.registerCycle();
        })
        .catch(e => {
          ++this.initializeUserRetryCounter;
          setTimeout(() => {
            this.initialize();
          }, 5000 * this.initializeUserRetryCounter);
        })
        .finally(() => {
          this.isLoadingLoggedInAgentData = false;
        });
    },

    handleServerSideError() {
      this.initialize();
    },

    async restoreActiveInteractions() {
      try {
        this.isRestoringInteractions = true;
        const { data: interactions } = await AgentService.getCurrentAgentInteractions();

        // Reload active/offered interactions
        interactions.map(this.tweakInteractionStates).forEach(rawInteraction => {
          let interaction = new EcsInteraction(rawInteraction);
          const elements = rawInteraction.elements
            .filter(element => element.agentId === this.loggedInAgent.id)
            .map(element => ({
              ...element,
              interactionId: interaction.interactionId,
              elementId: element.id,
              entryTime: interaction.timing.entryTime,
              waitingTime: interaction.timing.waitingTime
            }));

          if (interaction.isOffered || interaction.isPreview) {
            interaction = { ...interaction };
            interaction = new EcsInteraction(interaction);
            this.upsertPendingInteraction(interaction);
          } else {
            this.restoreActiveInteraction(interaction, elements);
          }
        });

        let activeElements = [];
        interactions.forEach(interaction => {
          activeElements = [...activeElements, ...this.getElementIdsByInteractionId(interaction)];
        });
        this.clearInactiveElementsAnsweredAt(activeElements);
      } catch (error) {
        this.$root.$emit('snackbar-message', {
          message: this.$t('errorOnReloadActiveInteractions'),
          type: 'error'
        });
      } finally {
        this.isRestoringInteractions = false;
      }
    },

    restoreActiveInteraction(interaction, elements) {
      const existingInteraction = this.getActiveInteraction(interaction.interactionId);

      // Interaction
      if (existingInteraction) {
        this.editActiveInteraction(interaction);
      } else {
        this.addActiveInteraction(interaction);
      }

      // Elements
      this.restoreElements(interaction, elements);

      // Conversations
      if (interaction.isChat) elements.forEach(this.restoreConversation);

      // Emails
      if (interaction.isEmail) elements.forEach(this.restoreEmail);
    },

    setAutoOffHook() {
      if (this.preferencesSettings.autoOffHookState) {
        AgentService.enableAutoOffHook();
      } else {
        AgentService.disableAutoOffHook();
      }
    },

    /**
     * Conference Scenario.
     * A consulted agent does not update its elements to conference state.
     * it only receives an interaction event 'In Conference', but on API retrieval it
     * comes with a 'handling' state.
     */
    tweakInteractionStates(interaction) {
      if (interaction.elements.length === 3) {
        if (interaction.elements[0].elementStateId === ELEMENT_STATE.conferenced) {
          interaction.interactionStateId = INTERACTION_STATE.inConference;
          interaction.interactionStateName = 'In Conference';
        }
      }
      return interaction;
    },

    restoreElements(interaction, elements) {
      elements.forEach(element => {
        element.answeredAt = this.getElementAnsweredAt(element.elementId);
      });

      const [mainElement, ...secondaryElements] = elements;

      const currentElements = this.getRawElementsForInteraction(interaction);
      currentElements
        .filter(currentElement => !elements.find(element => element.elementId === currentElement.elementId))
        .forEach(element => this.removeElement(element));

      if (interaction.isInternal) {
        // Internal interaction
        const contact = interaction.connectedPartiesListNew
          ? interaction.connectedPartiesListNew[0]
          : interaction.connectedPartiesList
          ? interaction.connectedPartiesList[0]
          : {};
        this.addElement({
          ...mainElement,
          receptor: {
            type: CONTACT_TYPE[(contact.partyType || '').toLowerCase()],
            id: contact.id,
            name: contact.name
          }
        });
      } else if (interaction.isConsultRequestor) {
        // TODO Review refresh consulted agent.
        if (secondaryElements.length === 0) {
          // Abandoned Conversation Element
          const abandonedConversation = this.getConversationsForInteraction(interaction.interactionId)[0];
          this.addElement({
            interactionId: interaction.interactionId,
            elementId: abandonedConversation ? abandonedConversation.elementId : 'abandoned-and-no-conversation',
            elementStateId: ELEMENT_STATE.abandoned,
            elementStateName: 'Abandoned',
            receptor: interaction.customer
          });

          secondaryElements.push(mainElement);
        } else {
          this.addElement({
            ...mainElement,
            receptor: interaction.customer
          });
        }

        // Consult tab(s)
        secondaryElements.forEach(element => {
          this.addElement({
            ...element,
            receptor: {
              id: interaction.consult.receiver.id,
              name: interaction.consult.receiver.name,
              type: interaction.consult.receiver.type,
              endPoint: interaction.consult.receiver.endPoint
            }
          });
        });
      } else if (interaction.isConsultReceiver) {
        this.addElement({
          ...mainElement,
          elementStateId: interaction.isConference ? ELEMENT_STATE.conferenced : mainElement.elementStateId,
          elementStateName: interaction.isConference ? 'Conferenced' : mainElement.elementStateName,
          receptor: {
            id: interaction.consult.requestor.id,
            name: interaction.consult.requestor.name,
            type: CONTACT_TYPE.agent,
            endPoint: mainElement.origin
          }
        });
      } else {
        // Web customer - Basic interaction
        this.addElement({
          ...mainElement,
          receptor: interaction.customer
        });
      }
    },

    initializeSettings() {
      this.isLoadingAppSettings = true;
      this.requestCurrentUserSettings()
        .then(settings => {
          this.$root.$i18n.locale = settings.applicationSettings.language;
          ThemeUtility.build(settings.theme);
        })
        .catch(() => this.handleLogout())
        .finally(() => {
          this.isLoadingAppSettings = false;
        });
    },

    initializeSocket() {
      if (this.isAuthenticatingEvents || !this.loggedInAgent) {
        return;
      } else {
        this.socketReconnectionAttemptCounter = 0;
        this.isAuthenticatingEvents = true;
      }

      const { id, account, pbxEnterpriseID: enterpriseId } = this.loggedInAgent;
      const orgShortName = this.orgShortName;
      const orgId = this.orgId;

      const browserId = sessionStorage.getItem('AWC_BROWSER_ID');

      this.$socket.open();
      this.$socket.emit('authentication', {
        agentId: id,
        account,
        enterpriseId,
        orgId,
        orgName: orgShortName,
        orgKafka: this.orgKafkaTopicName,
        browserId
      });
    },

    initializeIncomingInteractionRingtones() {
      window.AudioContext = window.webkitAudioContext || window.AudioContext || window.mozAudioContext;
      this.requestIncomingInteractionRingtones();
    },

    initializeUI() {
      if (!this.$route.name) {
        this.$router.push('dashboards');
      }
      this.preventDragDropEvent();
      this.setupDesktopNotifications();
      this.initializeIncomingInteractionRingtones();
    },

    setPauseKPIEvents: debounce(function() {
      if (this.isAuthenticatingEvents || this.isLoadingAppSettings || !this.isRegistered) {
        if (this.preferencesSettings.pauseKPIEvents) {
          // Wait for socket to be connected and settings to be loaded to retry to set pauseKPIEvents.
          setTimeout(this.setPauseKPIEvents, 1000);
        }
      } else if (this.preferencesSettings.pauseKPIEvents !== undefined) {
        this.$socket.emit('setPreferences', { pauseKPIEvents: this.preferencesSettings.pauseKPIEvents });
      }
    }, 500),

    async login(jwt) {
      if (!jwt) return;

      try {
        this.isAuthenticatingToken = true;
        const username = jwtDecode(jwt).username;

        if (process.env.VUE_APP_ELASTIC_APM_ENABLED === 'true') {
          this.span = this.$apm.startSpan('jwt-login', username);
        }

        const options = { headers: { Authorization: `Bearer ${jwt}`, username } };
        const loginResponse = await this.loginAgent(options);

        if (loginResponse) {
          if (loginResponse.agentInformation.isWallboard) {
            this.isWallboard = true;
          } else {
            this.initializeSocket();
            this.$root.$i18n.locale = loginResponse.agentSettings.applicationSettings.language;
            ThemeUtility.build(loginResponse.agentSettings.theme);
          }
        } else if (this.$refs.login) {
          // Wrong token
          this.$refs.login.showLoginFailure();
        }
        if (process.env.VUE_APP_ELASTIC_APM_ENABLED === 'true') {
          this.$apm.setUserContext({ username });
        }
      } catch (error) {
        this.handleLogout(
          this.$t('logout.errors.authenticationFailed.message'),
          this.$t('logout.errors.authenticationFailed.title')
        );
      } finally {
        this.isRegistered = true;

        const [cleanUrl] = window.location.href.split('?');
        window.history.replaceState({ path: cleanUrl }, '', cleanUrl);
        this.isAuthenticatingToken = false;
        this.span && this.span.end();
      }
    },

    async beforeunload(event) {
      if (this.getActiveTelephonyInteractions.length > 0) {
        event.preventDefault();
        event.returnValue = '';
        return;
      }

      if (this.loggedInAgent) {
        await this.logoutReason({ reason: 'Browser Refresh.' });
      }
    },

    async fullLogOut({
      reason,
      isUnauthorized,
      isServiceUnavailable,
      isSessionExpired,
      runningOnTeams,
      isLicenseLimit
    }) {
      await this.logoutReason({ reason });
      const error = {};

      console.error({ reason, isUnauthorized, isServiceUnavailable, isSessionExpired, runningOnTeams, isLicenseLimit });

      if (isLicenseLimit) {
        error.title = this.$t('logout.errors.licenseLimit.title');
        error.message = this.$t('logout.errors.licenseLimit.message');
      } else if (isUnauthorized) {
        error.title = this.$t('logout.errors.unauthorized.title');
        error.message = this.$t('logout.errors.unauthorized.message');
      } else if (isServiceUnavailable) {
        error.title = this.$t('logout.errors.unavailable.title');
        error.message = this.$t('logout.errors.unavailable.message');
      } else if (isSessionExpired && runningOnTeams) {
        error.title = this.$t('logout.errors.expired.title');
        error.message = this.$t('logout.errors.expired.redirect', { seconds: 5 });
      } else if (isSessionExpired) {
        error.title = this.$t('logout.errors.expired.title');
        error.message = this.$t('logout.errors.expired.message');
      }

      this.handleLogout(error.message, error.title);
    },

    logout() {
      ThemeUtility.removeTheme();

      if (this.$socket) {
        this.$socket.disconnect();
      }

      this.$root.$emit('soft-phone-action', { action: ACTIONS.destroy });

      this.removeLoggedInAgent();
      this.removeUserSettings();

      this.clearRegisterRetryRef();
      this.unregisterSoftPhone();

      if ('Notification' in window) {
        window.removeEventListener('blur', this.enableDesktopNotifications);
        window.removeEventListener('focus', this.disableDesktopNotifications);
      }
      window.removeEventListener('dragover', this.listenerFn, false);
      window.removeEventListener('drop', this.listenerFn, false);

      sessionStorage.removeItem('sessionId');
      sessionStorage.removeItem('agent');

      this.clearStats();
      this.clearOrganizationData();
    },

    // This method is to prevent the drag and drop event in windows or tab
    // and prevent opens external files or images in any place of applications
    preventDragDropEvent() {
      window.addEventListener('dragover', this.listenerFn, false);
      window.addEventListener('drop', this.listenerFn, false);
    },

    handleIdle() {
      this.$router.push('dashboards');
    },

    /**
     * Routes the user to the correct application tab (Telephony/Chat/Email)
     * @param {EcsInteraction} interaction
     */
    routeUserToInteractionTab(eventPayload) {
      if (eventPayload.interactionMediaType.toUpperCase() === MEDIA_TYPE_TELEPHONY) {
        if (this.$router.currentRoute.name !== 'Calls') {
          this.$router.push('calls').catch(e => console.error(e));
        }
      } else if (eventPayload.interactionMediaType.toUpperCase() === MEDIA_TYPE_CHAT) {
        if (this.$router.currentRoute.name !== 'Chats') {
          this.$router.push('chats').catch(e => console.error(e));
        }
      } else if (eventPayload.interactionMediaType.toUpperCase() === MEDIA_TYPE_EMAIL) {
        if (this.$router.currentRoute.name !== 'Emails') {
          this.$router.push('emails').catch(e => console.error(e));
        }
      } else if (eventPayload.interactionMediaType.toUpperCase() === MEDIA_TYPE_SMS) {
        if (this.$router.currentRoute.name !== 'Sms') {
          this.$router.push('sms').catch(e => console.error(e));
        }
      }
    },

    enableDesktopNotifications() {
      this.isDesktopNotificationEnabled = 'Notification' in window && Notification.permission === 'granted';
    },

    disableDesktopNotifications() {
      this.isDesktopNotificationEnabled = false;
    },

    handleMiniMode(isDisabled) {
      if (this.$route.name === 'KPISubscriptions') {
        this.$refs.ecsInteractionContainer.$el.style.marginLeft = '60px';
      } else {
        this.$refs.ecsInteractionContainer.$el.style.marginLeft = isDisabled ? '375px' : '60px';
      }
    },

    setupDesktopNotifications() {
      if ('Notification' in window) {
        if (Notification.permission !== 'granted') {
          Notification.requestPermission(permission => {
            // Whatever the user answers, we make sure that browser stores the information
            if (Notification.permission !== permission) {
              Notification.permission = permission;
            }

            this.isDesktopNotificationEnabled = permission === 'granted';
          });
        }

        window.addEventListener('blur', this.enableDesktopNotifications);
        window.addEventListener('focus', this.disableDesktopNotifications);
      }
    },

    async getLastSelectedTerminal() {
      if (!this.agentStateSettings) {
        // Wait for agent state settings to be loaded by vuex.
        await new Promise(resolve => setTimeout(resolve, 500));
        return this.getLastSelectedTerminal();
      }

      const agentStateSettings = this.agentStateSettings && this.agentStateSettings.lastSelectedTerminal;
      const agentEndpoint = {
        terminal: agentStateSettings && agentStateSettings.terminalURI,
        useInternalPhone: !!(agentStateSettings && agentStateSettings.useInternalPhone) || undefined
      };

      let isValidTerminal = false;

      this.telephoneTerminals.forEach(terminal => {
        if (terminal.userName === agentEndpoint.terminal || terminal.uri === agentEndpoint.terminal) {
          isValidTerminal = true;
        }
      });

      isValidTerminal = agentStateSettings && agentStateSettings.terminalType === 'Other' ? true : isValidTerminal;

      return isValidTerminal ? agentEndpoint : this.getDefaultTerminal();
    },

    getDefaultTerminal() {
      const defaultTerminal = this.telephoneTerminals.find(terminal => terminal.isDefault);
      return defaultTerminal
        ? {
            terminal:
              defaultTerminal.terminalType === TERMINAL_TYPES.HPBX_PHONE
                ? defaultTerminal.uri
                : defaultTerminal.userName || defaultTerminal.uri,
            useInternalPhone: defaultTerminal.terminalType === TERMINAL_TYPES.INTERNAL_PHONE ? true : undefined
          }
        : null;
    },

    async registerUser({ account, terminal = null, callback = null }) {
      HttpRequestHandler.setSessionUsername(account);

      const agentTerminal = terminal
        ? { terminal: terminal.terminalURI, useInternalPhone: terminal.useInternalPhone }
        : await this.getLastSelectedTerminal();

      const browserId = sessionStorage.getItem('AWC_BROWSER_ID');
      const registerResponse = AgentService.register({ instanceId: browserId, ...agentTerminal });

      this.registerRequestRef = registerResponse
        .then(response => {
          this.setRegistrationId(browserId);
          callback && callback();
          return response;
        })
        .catch(error => {
          if (this.isRegistered) {
            this.removeEventTimingConstraintForAgentTerminal({ agentId: this.loggedInAgent.id });
            this.$root.$emit('snackbar-message', {
              message: error,
              type: 'error'
            });
          } else {
            this.handleLogout(
              this.$t('logout.errors.registrationFailed.message'),
              this.$t('logout.errors.registrationFailed.title')
            );
          }
        });

      return this.registerRequestRef;
    },

    clearRegisterRetryRef() {
      if (this.registerRetryRef) {
        clearTimeout(this.registerRetryRef);
        this.registerRetryRef = null;
      }
    },

    /**
     * Retries up to 5 times if an error occurred while registering current agent.
     * If it fails 5 times it will retry the whole cycle after 1 minute.
     **/
    registerCycle() {
      const agent = JSON.parse(sessionStorage.getItem('agent'));
      if (!agent || !agent.account) {
        return;
      }

      this.isRegistering = true;

      return this.registerUser({ account: agent.account })
        .then(data => {
          this.registerRetryCounter = 0;
          this.isRegistered = true;
          this.isRegistering = false;

          this.clearRegisterRetryRef();
          this.requestApplicationData();
          this.$root.$emit('request-contacts');
          this.restoreActiveInteractions();
          this.initializeUI();
          this.setAutoOffHook();
          this.loadAgentStateBatch();
          this.initializeSoftPhone();
          this.initializeMsTeamsApp();
          this.clearDirectCallFlag();

          return data;
        })
        .catch(async () => {
          this.registerRetryCounter++;
          if (this.registerRetryCounter >= 5) {
            this.registerRetryCounter = 0;
            await this.logoutReason({
              reason: `Max register attempts (5). eAgent automated log out.`
            });
            this.handleLogout(
              this.$t('logout.errors.registrationFailed.message'),
              this.$t('logout.errors.registrationFailed.title')
            );
          } else {
            setTimeout(() => {
              this.registerCycle();
            }, 500);
          }
        })
        .finally(() => {
          this.isRegistering = false;
        });
    },

    initializeMsTeamsApp() {
      this.subscribeToPresenceChanges();
    },

    clearDirectCallFlag() {
      return AgentService.markAsOnDirectCall(false);
    },

    requestApplicationData() {
      // Fetch supervised business process list
      if (this.isSupervisor) {
        this.requestSupervisedBusinessProcesses();
        this.requestCampaigns();
      }

      this.requestAvailableBusinessProcesses()
        .then(() => {
          this.requestAgentInteractionHistory().catch(console.log);
        })
        .catch(console.log);
    },

    handleSupervisorTitle() {
      if (this.isSupervisor) {
        document.title = 'eSupervisor';
      }
    }
  }
};
</script>

<style src="./styles.scss" scoped lang="scss" />
